/**
 * Lightweight Umami analytics wrapper.
 *
 * - Custom events via Umami's track() API.
 * - No-ops gracefully when Umami is disabled (local dev, ad blockers).
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { config } from '@/config';

declare global {
  interface Window {
    umami?: {
      track: (eventOrProps?: string | Record<string, any>, data?: Record<string, any>) => void;
    };
  }
}

type AnalyticsEventData = Record<string, string | number | boolean | null | undefined>;

let currentAnalyticsUserId: string | null = null;

function sendToUserAnalytics(eventName: string, data?: AnalyticsEventData): void {
  if (!config.userAnalyticsEnabled || !config.userAnalyticsApiKey) {
    return;
  }

  const trackPath = config.userAnalyticsTrackPath.startsWith('/')
    ? config.userAnalyticsTrackPath
    : `/${config.userAnalyticsTrackPath}`;
  const endpoint = `${config.userAnalyticsApiUrl.replace(/\/$/, '')}${trackPath}`;
  const sanitizedPath = typeof window !== 'undefined'
    ? window.location.pathname.replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/<id>')
    : undefined;
  const payload = {
    user_id: currentAnalyticsUserId,
    event_type: eventName,
    event_category: 'custom',
    metadata: {
      ...data,
      page: sanitizedPath,
    },
  };

  fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': config.userAnalyticsApiKey,
    },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {
    // analytics transport failures should never break the app
  });
}

export function initUserAnalytics(
  userId: string,
  traits?: AnalyticsEventData,
): void {
  currentAnalyticsUserId = userId;
  sendToUserAnalytics('user_identified', traits);
}

/**
 * Track a custom event. Safe to call even if Umami is not loaded.
 */
export function trackEvent(
  name: string,
  data?: Record<string, string | number | boolean>,
): void {
  try {
    window.umami?.track(name, data);
    sendToUserAnalytics(name, data);
  } catch {
    // analytics should never break the app
  }
}

// ---------------------------------------------------------------------------
// Strategy creation funnel
// ---------------------------------------------------------------------------

/** User selected a strategy template */
export const trackTemplateSelected = (templateId: string) =>
  trackEvent('template_selected', { template_id: templateId });

/** First manual code edit in a session (fire once) */
export const trackStrategyEdited = () =>
  trackEvent('strategy_edited');

/** User selected a data source type */
export const trackDataSourceSelected = (source: 'exchange' | 'upload' | 'sample') =>
  trackEvent('data_source_selected', { source });

/** CSV upload completed */
export const trackDataUploadCompleted = (rowCount: number) =>
  trackEvent('data_upload_completed', { row_count: rowCount });

/** Backtest submitted */
export const trackBacktestSubmitted = (props: {
  strategy_type: string;
  data_source: string;
  template_used: string;
  ai_assisted: boolean;
  has_ga: boolean;
  device_type?: 'mobile' | 'tablet' | 'desktop';
}) => trackEvent('backtest_submitted', props);

/** Results page viewed */
export const trackResultsViewed = (jobId: string, hasGa: boolean) =>
  trackEvent('results_viewed', { job_id: jobId, has_ga: hasGa });

/** Results page viewed — extended payload for Results v2 (verdict, viewport,
 *  duplicate flag). Layered next to the legacy event so existing dashboards
 *  keep working; this fires alongside, not instead of, `trackResultsViewed`. */
export const trackResultsViewedV2 = (props: {
  job_id: string;
  verdict: 'promising' | 'inconclusive' | 'underperformed';
  viewport: 'mobile' | 'desktop';
  is_duplicate: boolean;
}) => trackEvent('results_viewed_v2', props);

/** All possible follow-up actions a user can take on a Results page. */
export type FollowupActionType =
  | 'refine'
  | 'widen'
  | 'rerun'
  | 'deploy'
  | 'edit_code'
  | 'new_strategy'
  | 'leave';

/** A user took (or did not take) a follow-up action on the Results page.
 *  `target` is set only for `refine` (the parameter name being changed).
 *  `dwell_ms` is the time between page mount and the action. `leave` fires
 *  on visibilitychange/beforeunload when no other follow-up was recorded
 *  within 60 seconds. */
export const trackFollowupAction = (props: {
  type: FollowupActionType;
  target?: string;
  dwell_ms: number;
}) => trackEvent('followup_action', props);

/** The user clicked an iterate verb (refine/widen/rerun) on a Results page
 *  hero, which routes them into a fresh ResearchInterview re-entry
 *  conversation seeded with the parent run's context. Fires at click time.
 *  Paired with `trackIterateConfirmed` when the user clicks Run. */
export const trackIterateStarted = (props: {
  parent_job_id: string;
  kind: 'refine' | 'widen' | 'rerun' | 'edit' | 'assistant';
  num_changes: number;
}) => trackEvent('iterate_started', props);

/** The user clicked Run on a backtest setup form that arrived prefilled with
 *  an `IterateDelta`. Together with `iterate_started` this lets us measure
 *  the funnel from verb click → committed run. */
export const trackIterateConfirmed = (props: {
  parent_job_id: string;
  kind: 'refine' | 'widen' | 'rerun' | 'edit' | 'assistant';
  num_changes: number;
}) => trackEvent('iterate_confirmed', props);

/** User selected an option card on the IteratePage (before clicking Run).
 *  Tracks intent; paired with `trackIterateStarted` which fires on Run click. */
export const trackIterateOptionSelected = (props: {
  parent_job_id: string;
  kind: 'refine' | 'widen' | 'rerun';
}) => trackEvent('iterate_option_selected', props);

/** User clicked the upgrade CTA on IteratePage because the selected option is
 *  locked on their plan. `reason` distinguishes GA gate from history limit. */
export const trackIterateUpgradeClicked = (props: {
  parent_job_id: string;
  reason: 'ga_required' | 'history_limit';
}) => trackEvent('iterate_upgrade_clicked', props);

/** Backtest completed */
export const trackBacktestCompleted = (strategy: string, durationSec: number) =>
  trackEvent('backtest_completed', { strategy, duration_sec: Math.round(durationSec) });

/** Backtest failed */
export const trackBacktestFailed = (errorType: 'syntax' | 'runtime' | 'timeout' | 'risk_halt' | 'unknown') =>
  trackEvent('backtest_failed', { error_type: errorType });

/** Strategy validation ran (syntax check) */
export const trackStrategyValidated = (valid: boolean) =>
  trackEvent('strategy_validated', { valid });

// ---------------------------------------------------------------------------
// AI assistant funnel
// ---------------------------------------------------------------------------

/** AI panel opened */
export const trackAIOpened = (mode: string, page: 'create' | 'results' = 'create') =>
  trackEvent('ai_assistant_opened', { mode, page });

/** User sent a message in AI assistant */
export const trackAIMessage = (mode: string) =>
  trackEvent('ai_message_sent', { mode });

/** AI auto-applied generated/improved code */
export const trackAIStrategyGenerated = (mode: string) =>
  trackEvent('ai_strategy_generated', { mode });

/** The AI mode the system inferred for an outgoing request (implicit routing). */
export const trackAIModeInferred = (mode: string) =>
  trackEvent('ai_mode_inferred', { mode });

/** User corrected the inferred AI mode via a visible escape hatch.
 *  `surface` identifies which affordance triggered the override. */
export const trackAIModeOverridden = (from: string, to: string, surface: string) =>
  trackEvent('ai_mode_overridden', { from, to, surface });

/** User submitted AI feedback (thumbs up/down) */
export const trackAIFeedback = (mode: string, sentiment: 'positive' | 'negative') =>
  trackEvent('ai_feedback_submitted', { mode, sentiment });

/** AI proposal accepted (Apply & open in Lab). */
export const trackProposalApplied = (
  surface: string,
  proposalMode: 'incremental' | 'bundled' | 'unknown',
) => trackEvent('ai_proposal_applied', { surface, proposal_mode: proposalMode });

/** "Rerun with same settings" chip clicked on results. */
export const trackResultsRerunSameSettings = (jobId: string) =>
  trackEvent('results_rerun_same_settings', { job_id: jobId });

/** A rerun request was successfully submitted; carries the new and parent job IDs. */
export const trackRerunSubmitted = (parentJobId: string, newJobId: string) =>
  trackEvent('rerun_submitted', { parent_job_id: parentJobId, new_job_id: newJobId });

/** Viewer landed on a results page whose run has a parent_job_id (i.e. is itself a rerun). */
export const trackRerunCompletedViewed = (jobId: string, parentJobId: string) =>
  trackEvent('rerun_completed_viewed', { job_id: jobId, parent_job_id: parentJobId });

/** Model declared a proposal_mode in its META block (incremental or bundled). */
export const trackProposalModeDeclared = (
  mode: 'incremental' | 'bundled',
  surface: string,
) => trackEvent('ai_proposal_mode_declared', { proposal_mode: mode, surface });

/** Client downgraded a model-declared `bundled` proposal to `incremental` based on
 *  block-size heuristic. Server applies the same rule independently for ai_messages. */
export const trackProposalModeDowngraded = (reason: string, surface: string) =>
  trackEvent('ai_proposal_mode_downgraded', { reason, surface });

/** Results -> Strategy Lab navigation from a proposal chip. */
export const trackResultsOpenInLab = (jobId: string) =>
  trackEvent('results_open_in_lab', { job_id: jobId });

/** Detected a new client-side AI thread when an existing one was available
 *  (signal that the unified-conversation contract slipped). */
export const trackDuplicateThreadCreated = (surface: string) =>
  trackEvent('ai_duplicate_thread_created', { surface });

// ---------------------------------------------------------------------------
// Optimization funnel
// ---------------------------------------------------------------------------

/** GA optimization started */
export const trackGAOptimizationStarted = (populationSize: number, generations: number) =>
  trackEvent('ga_optimization_started', { population_size: populationSize, generations });

/** Walk-forward analysis started */
export const trackWalkforwardStarted = (windows: number) =>
  trackEvent('walkforward_started', { windows });

// ---------------------------------------------------------------------------
// Export + general
// ---------------------------------------------------------------------------

/** Results exported */
export const trackExport = (format: string) =>
  trackEvent('results_exported', { format });

/** Results shared (link copied) */
export const trackResultsShared = (method: string) =>
  trackEvent('results_shared', { method });

/** User changed settings */
export const trackSettingsChanged = (setting: string) =>
  trackEvent('settings_changed', { setting });

// ---------------------------------------------------------------------------
// Discover (v1 — wizard + archetype library for "no idea yet" users)
// ---------------------------------------------------------------------------
// Note: payloads must NOT include the user's free-text wizard answers — only
// enum / bucket values. Free-text leakage into analytics was flagged by the
// Security council as launch-blocking.

export const trackDiscoverWizardStarted = () =>
  trackEvent('discover_wizard_started');

export const trackDiscoverWizardStepCompleted = (step: number) =>
  trackEvent('discover_wizard_step_completed', { step });

export const trackDiscoverWizardCompleted = (recommendedArchetypeId: string) =>
  trackEvent('discover_wizard_completed', { archetype_id: recommendedArchetypeId });

export const trackDiscoverArchetypeViewed = (archetypeId: string) =>
  trackEvent('discover_archetype_viewed', { archetype_id: archetypeId });

export const trackDiscoverArchetypeForked = (archetypeId: string, source: 'wizard' | 'browse') =>
  trackEvent('discover_archetype_forked', { archetype_id: archetypeId, source });

export const trackDiscoverSkippedToEmptyEditor = (atStep: number) =>
  trackEvent('discover_skipped_to_empty_editor', { at_step: atStep });

// ---------------------------------------------------------------------------
// Deployments
// ---------------------------------------------------------------------------

/** New deployment created */
export const trackDeploymentCreated = (strategy: string, mode: 'paper' | 'live') =>
  trackEvent('deployment_created', { strategy, mode });

/** Deployment status changed (pause / stop / activate) */
export const trackDeploymentStatusChanged = (id: string, newStatus: 'active' | 'paused' | 'stopped') =>
  trackEvent('deployment_status_changed', { deployment_id: id, status: newStatus });

// ---------------------------------------------------------------------------
// Live-trading surfaces: Strategies / Exchanges / Deployments (council PR-A)
// All payloads use enums/ids only — never free-text names — to keep telemetry
// clean and avoid leaking user-authored strings.
// ---------------------------------------------------------------------------

/** Saved Strategies page viewed. `count` = strategies returned for the tenant. */
export const trackStrategiesViewed = (count: number) =>
  trackEvent('strategies_viewed', { count });

/** A lifecycle action taken on a saved strategy. */
export const trackStrategyAction = (action: 'rename' | 'toggle_active' | 'archive' | 'delete') =>
  trackEvent('strategy_action', { action });

/**
 * Exchanges page viewed. `state` describes which onboarding variant
 * rendered. `paper_onboarding`/`paper_at_cap` replaced the old
 * `explorer_upgrade` wall (D-FREE-PAPER, design council 2026-10-15):
 * a paper-only tier is no longer locked out of Exchanges entirely.
 */
export const trackExchangesViewed = (
  state: 'paper_onboarding' | 'paper_at_cap' | 'onboarding' | 'returning',
) => trackEvent('exchanges_viewed', { state });

/** An exchange-connection action taken from the Exchanges/Settings surface. */
export const trackExchangeAction = (action: 'add' | 'test' | 'delete') =>
  trackEvent('exchange_action', { action });

/** Live Trading (Deployments) page viewed. `count` = deployments for the tenant. */
export const trackDeploymentsViewed = (count: number) =>
  trackEvent('deployments_viewed', { count });

// ---------------------------------------------------------------------------
// Landing page conversions
// ---------------------------------------------------------------------------

/**
 * CTA clicked on a landing section. Marketing ruling M1 (2026-10-20): the
 * enterprise/"Contact Sales" CTA is gone (tier hidden); 'firm' is the
 * design-partner invitation and 'managed_waitlist' the "tell me when live
 * trading opens" link on the Managed card.
 */
export const trackLandingCTA = (
  section: 'hero' | 'pricing' | 'cta' | 'nav' | 'features' | 'firm' | 'managed_waitlist',
) => trackEvent('landing_cta_clicked', { section });

/**
 * Sign-up "what brought you here?" answer (marketing ruling M1, kill
 * criterion K-M1: 50%+ of the first 20 signups answering "bring my own
 * strategy" or "an AI that finds trades" switches the headline).
 */
export const trackSignUpReason = (reason: string) =>
  trackEvent('sign_up_reason', { reason });

/** Pricing tier CTA clicked */
export const trackPricingPlanClicked = (plan: string) =>
  trackEvent('pricing_plan_clicked', { plan });

// ---------------------------------------------------------------------------
// Lineage / AI summary card (Phase 4)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Lineage interactions (Phase 5)
// ---------------------------------------------------------------------------

/** User authored a new lineage note. */
export const trackResultsNoteAdded = (jobId: string, noteType: string) =>
  trackEvent('results.lineage.note_added', { job_id: jobId, note_type: noteType });

/** User opened the manual reparent dialog. */
export const trackResultsReparentOpened = (jobId: string) =>
  trackEvent('results.lineage.reparent.opened', { job_id: jobId });

/** Manual reparent succeeded. */
export const trackResultsReparentSubmitted = (
  jobId: string,
  outcome: 'success' | 'rejected',
) => trackEvent('results.lineage.reparent.submitted', { job_id: jobId, outcome });

/** User opened the compare-to-parent diff. */
export const trackResultsCompareParentOpened = (jobId: string) =>
  trackEvent('results.lineage.compare_parent.opened', { job_id: jobId });

/** User opened the subtree (all branches off this experiment) view. */
export const trackResultsSubtreeViewed = (rootJobId: string, nodeCount: number) =>
  trackEvent('results.lineage.subtree.viewed', {
    root_job_id: rootJobId,
    node_count: nodeCount,
  });

// ---------------------------------------------------------------------------
// Authentication funnel
// ---------------------------------------------------------------------------

/** Sign-in page shown / entered */
export const trackSignIn = () =>
  trackEvent('sign_in_started');

/** Sign-up page shown / entered */
export const trackSignUp = (plan?: string) =>
  trackEvent('sign_up_started', plan ? { plan } : undefined);

// ---------------------------------------------------------------------------
// Onboarding
// ---------------------------------------------------------------------------

/** Single onboarding step completed */
export const trackOnboardingStep = (step: number, name: string) =>
  trackEvent('onboarding_step_completed', { step, name });

/** Full onboarding wizard completed */
export const trackOnboardingCompleted = () =>
  trackEvent('onboarding_completed');

// ---------------------------------------------------------------------------
// Guided tours (driver.js) — Tour Mechanic v2 telemetry (Phase 0)
// ---------------------------------------------------------------------------
// Instrumentation on the EXISTING tour system. These events are the data gate
// for deciding which tours/steps survive the v2 consolidation. `journey`
// identifies the tour (onboarding | config | loading | results | workflow).

/** A tour was started (auto or user-initiated). */
export const trackTourStarted = (journey: string, trigger: 'auto' | 'manual') =>
  trackEvent('tour.start', { journey, trigger });

/** A tour step became visible. `index` is 0-based within the available steps. */
export const trackTourStepView = (journey: string, stepId: string, index: number) =>
  trackEvent('tour.step_view', { journey, step_id: stepId, index });

/** A step could not be anchored because its target element was absent on the
 *  current route — the silent-failure signal the council called out. */
export const trackTourStepAnchorMissing = (journey: string, stepId: string, pathname?: string) =>
  trackEvent('tour.step_anchor_missing', { journey, step_id: stepId, ...(pathname && { pathname }) });

/** A tour ran to its last step (Done/Next on the final step). */
export const trackTourCompleted = (journey: string) =>
  trackEvent('tour.complete', { journey });

/** A tour was dismissed before completion (close / overlay click / Esc). */
export const trackTourDismissed = (journey: string) =>
  trackEvent('tour.dismiss', { journey });

// ---------------------------------------------------------------------------
// Documentation page
// ---------------------------------------------------------------------------
// Global trackPageView already captures /docs hits; these add SECTION-level
// granularity + silent-failure signals. Payloads carry slugs/enums only — no
// free-text (Security gate).

/** How the user arrived at the active docs section. */
export type DocsSectionSource = 'nav' | 'deeplink' | 'default';

/** A documentation section became active. */
export const trackDocsSectionView = (slug: string, source: DocsSectionSource) =>
  trackEvent('docs.section_view', { slug, source });

/** An inbound `?section=` slug did not match any known section (broken link). */
export const trackDocsSectionNotFound = (requestedSlug: string) =>
  trackEvent('docs.section_not_found', { requested_slug: requestedSlug });

/** User copied a code block from a docs section. */
export const trackDocsCodeCopied = (slug: string, lang: string) =>
  trackEvent('docs.code_copied', { slug, lang });

// ---------------------------------------------------------------------------
// Strategy deployment
// ---------------------------------------------------------------------------

/** Strategy promoted to live/paper trading from a backtest result */
export const trackStrategyDeployed = (strategy: string, mode: 'paper' | 'live') =>
  trackEvent('strategy_deployed', { strategy, mode });

/**
 * Feed-status badge transition (data heartbeat). Powers the ff_data_heartbeat
 * kill criterion: the false-red rate is unmeasurable without transitions.
 */
export const trackFeedStatusChanged = (deploymentId: string, from: string | null, to: string) =>
  trackEvent('feed_status_changed', { deployment_id: deploymentId, from: from ?? 'none', to });

// ---------------------------------------------------------------------------
// Research interview funnel
// ---------------------------------------------------------------------------

/** User landed on the research interview page and the opening AI message loaded. */
export const trackInterviewStarted = (props?: { device_type?: 'mobile' | 'tablet' | 'desktop' }) =>
  trackEvent('research_interview_started', props ?? {});

/** AI proposed a hypothesis (<<<HYPOTHESIS>>> marker emitted). */
export const trackHypothesisProposed = (props: { exchange_count: number }) =>
  trackEvent('research_hypothesis_proposed', props);

/** User confirmed the proposed hypothesis and is proceeding to the Strategy Lab. */
export const trackHypothesisConfirmed = (props: {
  hypothesis_length: number;
  prompt_version?: number;
  is_iterate?: boolean;
  parent_job_id?: string;
}) => trackEvent('research_hypothesis_confirmed', props);

/** AI presented quick-reply suggestion chips (interview or iterate loop).
 *  Joined with `research_option_picked` + the child job's verdict, this is the
 *  telemetry that answers whether AI suggestions beat random parameter
 *  perturbation out of sample (quant council ruling 2026-07-03). */
export const trackResearchSuggestionsShown = (props: {
  prompt_version: number;
  is_iterate: boolean;
  num_options: number;
  option_labels: string;
  parent_job_id?: string;
}) => trackEvent('research_suggestions_shown', props);

/** User picked one of the AI's quick-reply suggestion chips. */
export const trackResearchOptionPicked = (props: {
  prompt_version: number;
  is_iterate: boolean;
  option_label: string;
  option_index: number;
  parent_job_id?: string;
}) => trackEvent('research_option_picked', props);

/** User clicked "Adjust it" — rejected the proposed hypothesis. */
export const trackHypothesisRejected = () =>
  trackEvent('research_hypothesis_rejected');

/** User clicked "Skip for now" and navigated away from the interview. */
export const trackInterviewSkipped = (props: { exchange_count: number }) =>
  trackEvent('research_interview_skipped', props);

/** User hit the turn limit (10 exchanges) and was shown the manual-ready prompt. */
export const trackTurnLimitReached = (props: { exchange_count: number }) =>
  trackEvent('research_turn_limit_reached', props);

/** User's first real turn in the interview (typed or via a starter chip) --
 *  the funnel's first-engagement signal, distinct from
 *  research_interview_started (welcome merely shown, no user action yet). */
export const trackResearchFirstMessageSent = () =>
  trackEvent('research_first_message_sent');

/** The interview's start_workflow tool call resulted in a real workflow
 *  being created (POST /api/workflows succeeded) -- the funnel's completion
 *  event. Joined with research_interview_started (by session) this is what
 *  gives a real completion/drop-off rate instead of a guess. */
export const trackResearchWorkflowLaunched = (props: {
  workflow_id: string;
  workflow_type: string;
  exchange_count: number;
}) => trackEvent('research_workflow_launched', props);

// ---------------------------------------------------------------------------
// Unified Research launch funnel (ff_unified_research_v1)
// ---------------------------------------------------------------------------

/** User landed on the new unified launch surface (LaunchLanding). */
export const trackLaunchFormViewed = () =>
  trackEvent('research_launch_viewed');

/** User submitted the launch form -- enums/ids only, never the idea box's
 *  own free text (matches this file's existing telemetry-privacy discipline).
 *  `max_drawdown_tolerance`/`success_definition` are the risk-comfort/
 *  success-definition clarifying questions (design council 2026-07-24) --
 *  always one of a small fixed enum, 'not_stated' when left as "Not sure".
 *  `success_definition` widened to the platform's real 7 fitness presets
 *  2026-08-11 (was a lossy 4-category enum that could never reach
 *  aggressive/max_sharpe/high_frequency -- see start_workflow.rs's module
 *  doc). This is the manual LaunchForm's own field, distinct from the AI
 *  interview's (which no longer asks this as a separate question at all). */
export const trackLaunchFormSubmitted = (props: {
  asset_class: string;
  has_idea: boolean;
  max_drawdown_tolerance: 'not_stated' | 'p10' | 'p20' | 'p30_plus';
  success_definition: 'not_stated' | 'balanced' | 'aggressive' | 'conservative' | 'max_sharpe' | 'max_profit' | 'low_drawdown' | 'high_frequency';
}) => trackEvent('research_launch_submitted', props);

/** The launched search's runner view mounted at /dashboard/research/session/:id. */
export const trackSessionRunnerViewed = (workflowId: string) =>
  trackEvent('research_session_viewed', { workflow_id: workflowId });

/** The launched search reached a terminal result_summary kind. */
export const trackSessionResult = (props: {
  workflow_id: string;
  kind: 'promising_stop' | 'natural_completion' | 'tested_not_promising' | 'max_iterations';
}) => trackEvent('research_session_result', props);

// ---------------------------------------------------------------------------
// SPA navigation
// ---------------------------------------------------------------------------

/** Route changed — fired on every page transition */
export const trackPageView = (page: string) =>
  trackEvent('page_view', { page });

// ---------------------------------------------------------------------------
// Sidebar navigation (design council PR A, 2026-10-14)
// ---------------------------------------------------------------------------
// Every sidebar item click, across the whole nav -- not just the
// mandate/library/proposals/deployments group -- so "is X still used?"
// questions have real click-through data instead of a guess. `group` is the
// NavGroup title the item was rendered under at click time (grouping can
// change independently of the item itself, e.g. the Strategy Pipeline
// regrouping in this same PR).

/** A sidebar nav item was clicked. */
export const trackSidebarNavClicked = (props: {
  label: string;
  path: string;
  group: string;
}) => trackEvent('sidebar_nav_clicked', props);
